import { Typography, Button } from '@mui/material';
import { FileViewer } from 'src/@carketa/file-viewer/FileViewer';
import GoogleMapComponent from 'src/@carketa/sales-packet/components/GoogleMapWithMarker';
import PushPinIcon from '@mui/icons-material/PushPin';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import React, { Dispatch, SetStateAction } from 'react';
import type { OrganizationSettings } from 'src/types/organizations/organizations';
import { CarketaDisclosure } from 'src/@carketa/sales-packet/components/CarketaDisclosure';

type VehicleDetailsSectionProps = {
  vehicle: any;
  pdfMode?: boolean;
  setSalesLeadOpen?: Dispatch<SetStateAction<boolean>> | undefined;
};

const VehicleDetailsSection = ({
  vehicle,
  pdfMode,
  setSalesLeadOpen,
}: VehicleDetailsSectionProps) => {
  const org_settings = vehicle.organization.settings as OrganizationSettings;
  const vehicleDetailsWidget = (icon: any, title: string, desc: string) => {
    return (
      <div className="flex items-start w-1/4">
        <div>
          <Typography sx={{ color: '#000' }}>{title}</Typography>
          <Typography
            sx={{ wordBreak: 'break-word', color: 'rgba(58, 53, 65, 0.68)' }}
            variant="caption"
          >
            {desc}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div
      id="vehicle-details"
      className="flex flex-col justify-center items-center gap-6"
    >
      <div className="w-full p-2">
        <div className="w-full max-w-[1000px] flex items-center justify-between xs:flex-col sm:flex-row">
          <div className="flex">
            <div>
              <h1 className="text-lg text-center">
                {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}
              </h1>
              <p className="text-sm gray-500 text-center">{vehicle.vin}</p>
            </div>
          </div>

          <div className="flex justify-center xs:flex-col sm:flex-row xs:mt-[10px] sm:mt-[0px]">
            {!org_settings.remove_price_on_sales_packet && (
              <div className="mr-4">
                <h1 className="text-xl text-center">${vehicle.listed_price}</h1>
                <p className="text-sm gray-500 text-center">
                  {vehicle.mileage}{' '}
                  {org_settings?.use_kilometers ? 'Kilometers' : 'Miles'}
                </p>
              </div>
            )}
            {!pdfMode && setSalesLeadOpen && (
              <Button
                variant="contained"
                sx={{
                  display: vehicle.location.lead_sales_email ? '' : 'none',
                  backgroundColor: '#56ca00 !important',
                  mt: { xs: '10px', sm: '0px' },
                  '&:hover': {
                    backgroundColor: '#56ca00 !important',
                    color: '#FFF',
                  },
                }}
                onClick={() => setSalesLeadOpen(true)}
              >
                CHECK AVAILABILITY
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="p-2 w-full">
        <div className="mb-12">
          <FileViewer
            id={vehicle?.id ?? ''}
            showPlaceholder={false}
            showThumbnails={false}
            allowUpload={false}
            showMenu={false}
            pdfMode={pdfMode}
          />
        </div>
      </div>
      <div className="w-full p-2">
        <div className="mb-8 w-full">
          <Typography variant="h6" sx={{ mb: 5, color: '#000' }}>
            Vehicle Details
          </Typography>
          <div className="flex justify-between mb-6">
            {vehicleDetailsWidget(
              'engineIcon',
              'Engine',
              vehicle.engine ?? 'N/A'
            )}
            {vehicleDetailsWidget(
              'bodyStyleIcon',
              'Body Style',
              vehicle.body_style ?? 'N/A'
            )}
            {vehicleDetailsWidget(
              'exteriorIcon',
              'Exterior Color',
              vehicle.exterior_color ?? 'N/A'
            )}
          </div>
          <div className="flex justify-between mb-6">
            {vehicleDetailsWidget(
              'interiorIcon',
              'Interior',
              vehicle.interior_color ?? 'N/A'
            )}
            {vehicleDetailsWidget(
              'transmissionIcon',
              'Transmission',
              vehicle.transmission ?? 'N/A'
            )}
            {vehicleDetailsWidget('vinIcon', 'VIN', vehicle.vin ?? 'N/A')}
          </div>
          <div className="flex justify-between">
            {vehicleDetailsWidget(
              'stockNumberIcon',
              'Stock Number',
              vehicle.stocknumber ?? 'N/A'
            )}
            {vehicleDetailsWidget(
              'mileageIcon',
              'Mileage',
              vehicle.mileage ?? 'N/A'
            )}
            {vehicleDetailsWidget('trimIcon', 'Trim', vehicle.trim ?? 'N/A')}
          </div>
        </div>
        <div className="mb-8 w-full pl-2">
          <Typography sx={{ color: '#000' }} className="mb-4 text-md">
            Description
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(58, 53, 65, 0.68)' }}
          >
            {vehicle.description ?? 'N/A'}
          </Typography>
        </div>
      </div>
      <div className="p-2 w-full flex flex-col">
        <div className="flex gap-5 align-middle flex-wrap lg:flex-nowrap">
          <div className="flex flex-col p-3 w-full lg:w-1/3">
            <Typography variant="h6" sx={{ mb: 5, color: '#000' }}>
              Dealership Location
            </Typography>
            <p className="gray-500 text-sm mb-2">{vehicle.location.name} </p>
            <div className="flex items-center">
              <PushPinIcon sx={{ fontSize: 'medium', mr: 1 }} />
              <p className="gray-400 text-sm mb-1">
                {vehicle.location.stree1} {vehicle.location.city},{' '}
                {vehicle.location.state} {vehicle.location.zip}
              </p>
            </div>
            <div className="flex items-center">
              <EmailIcon
                sx={{
                  fontSize: 'medium',
                  mr: 1,
                  display: vehicle.location.contact_email ? '' : 'none',
                }}
              />
              <p className="gray-400 text-sm mb-1">
                {vehicle.location.contact_email}
              </p>
            </div>
            <div className="flex m-1 items-center">
              <PhoneIcon
                sx={{
                  fontSize: 'medium',
                  mr: 1,
                  display: vehicle.location.contact_phone ? '' : 'none',
                }}
              />
              <p className="gray-400 text-sm mb-1">
                {vehicle.location.contact_phone}
              </p>
            </div>
            <div className="flex m-1 items-center">
              <a href={`https://${vehicle.location.website}`}>
                <p className="gray-400 text-sm mb-1">
                  https://{vehicle.location.website}
                </p>
              </a>
            </div>
          </div>
          <div className="w-full lg:w-2/3">
            <GoogleMapComponent
              lat={vehicle.location.latitude}
              lng={vehicle.location.longitude}
            />
          </div>
        </div>
      </div>
      <div style={{ display: pdfMode ? '' : 'none' }}>
        <CarketaDisclosure />
      </div>
    </div>
  );
};

export default VehicleDetailsSection;
